/**
 * Admin customization draft for 雇入通知書兼雇用契約書 (invitation 管理者入力).
 * Values are flat string maps for easy later DB / AcroForm mapping.
 */

import {
  EMP_CONTRACT_TEXT_LIMITS,
  sanitizeBreakMinutes,
  sanitizeCompensationDays,
  sanitizeDailyWorkHours,
  sanitizeHour12,
  sanitizeMinute,
  sanitizeOvertimeHours,
  sanitizePercentRate,
  sanitizePersonName,
  sanitizeText,
  sanitizeYenAmount,
} from './employmentContractInputLimits';

export const EMPLOYMENT_CONTRACT_FORM_CODE = 'FF002';

export type EmploymentTermType = 'indefinite' | 'fixed';
export type YesNo = 'yes' | 'no';
export type AmPm = 'am' | 'pm';
export type PrefectureSuffix = '都' | '道' | '府' | '県';
export type CitySuffix = '区' | '市' | '郡';
export type RetirementPlan = 'chutaikyo' | 'kentaikyo' | 'none';
export type ScheduleSlot = '1' | '2';

export interface WorkScheduleRow {
  startHour: string;
  startMinute: string;
  startAmPm: AmPm;
  endHour: string;
  endMinute: string;
  endAmPm: AmPm;
  breakMinutes: string;
}

export interface EmploymentContractAdminValues {
  employmentTermType: EmploymentTermType;
  termStartDate: string; // yyyy-mm-dd
  termEndDate: string;
  termReason: string;

  workplacePrefectureName: string;
  workplacePrefectureSuffix: PrefectureSuffix;
  workplaceCityName: string;
  workplaceCitySuffix: CitySuffix;
  workplaceSite: string;

  jobDescription: string;

  shiftSystem: YesNo;
  overtimeWork: YesNo;
  /** Whether schedule pattern 1 is used on the contract. */
  schedule1Enabled: boolean;
  /** Whether schedule pattern 2 is used on the contract. */
  schedule2Enabled: boolean;
  schedule1: WorkScheduleRow;
  schedule2: WorkScheduleRow;
  dailyWorkHours: string;

  baseSalary: string;
  /** 補償給（日に付き） */
  compensationPerDay: string;
  overtimeWithinHours: string;
  overtimeWithinRate: string;
  overtimeOverHours: string;
  overtimeOverRate: string;
  statutoryHolidayRate: string;
  nonStatutoryHolidayRate: string;
  nightRate: string;
  retirementPlan: RetirementPlan;
  allowances: string;

  socialInsuranceHealth: boolean;
  socialInsurancePension: boolean;
  employmentInsurance: YesNo;

  siteManagerName: string;
  contractDate: string; // yyyy-mm-dd
}

export const emptyWorkSchedule = (): WorkScheduleRow => ({
  startHour: '',
  startMinute: '',
  startAmPm: 'am',
  endHour: '',
  endMinute: '',
  endAmPm: 'pm',
  breakMinutes: '',
});

export const defaultEmploymentContractAdminValues = (): EmploymentContractAdminValues => ({
  employmentTermType: 'indefinite',
  termStartDate: '',
  termEndDate: '',
  termReason: '',

  workplacePrefectureName: '',
  workplacePrefectureSuffix: '県',
  workplaceCityName: '',
  workplaceCitySuffix: '市',
  workplaceSite: '',

  jobDescription: '',

  shiftSystem: 'no',
  overtimeWork: 'no',
  schedule1Enabled: true,
  schedule2Enabled: false,
  schedule1: emptyWorkSchedule(),
  schedule2: emptyWorkSchedule(),
  dailyWorkHours: '',

  baseSalary: '',
  compensationPerDay: '',
  overtimeWithinHours: '',
  overtimeWithinRate: '',
  overtimeOverHours: '',
  overtimeOverRate: '',
  statutoryHolidayRate: '',
  nonStatutoryHolidayRate: '',
  nightRate: '',
  retirementPlan: 'chutaikyo',
  allowances: '',

  socialInsuranceHealth: false,
  socialInsurancePension: false,
  employmentInsurance: 'no',

  siteManagerName: '',
  contractDate: '',
});

/** Detect 雇入通知書兼雇用契約書 (catalog FF002). */
export function isEmploymentContractForm(
  formCode: string | undefined | null,
  nameJp?: string | null
): boolean {
  const code = (formCode ?? '').trim().toUpperCase();
  if (code === EMPLOYMENT_CONTRACT_FORM_CODE) return true;
  const name = nameJp ?? '';
  return name.includes('雇入通知') || name.includes('雇用契約書');
}

export function employmentContractToFlatMap(
  values: EmploymentContractAdminValues
): Record<string, string> {
  const s1 = values.schedule1;
  const s2 = values.schedule2;
  return {
    employmentTermType: values.employmentTermType,
    termStartDate: values.termStartDate,
    termEndDate: values.termEndDate,
    termReason: values.termReason,
    workplacePrefectureName: values.workplacePrefectureName,
    workplacePrefectureSuffix: values.workplacePrefectureSuffix,
    workplaceCityName: values.workplaceCityName,
    workplaceCitySuffix: values.workplaceCitySuffix,
    workplaceSite: values.workplaceSite,
    jobDescription: values.jobDescription,
    shiftSystem: values.shiftSystem,
    overtimeWork: values.overtimeWork,
    schedule1Enabled: values.schedule1Enabled ? '1' : '0',
    schedule2Enabled: values.schedule2Enabled ? '1' : '0',
    schedule1_startHour: s1.startHour,
    schedule1_startMinute: s1.startMinute,
    schedule1_startAmPm: s1.startAmPm,
    schedule1_endHour: s1.endHour,
    schedule1_endMinute: s1.endMinute,
    schedule1_endAmPm: s1.endAmPm,
    schedule1_breakMinutes: s1.breakMinutes,
    schedule2_startHour: s2.startHour,
    schedule2_startMinute: s2.startMinute,
    schedule2_startAmPm: s2.startAmPm,
    schedule2_endHour: s2.endHour,
    schedule2_endMinute: s2.endMinute,
    schedule2_endAmPm: s2.endAmPm,
    schedule2_breakMinutes: s2.breakMinutes,
    dailyWorkHours: values.dailyWorkHours,
    baseSalary: values.baseSalary,
    compensationPerDay: values.compensationPerDay,
    overtimeWithinHours: values.overtimeWithinHours,
    overtimeWithinRate: values.overtimeWithinRate,
    overtimeOverHours: values.overtimeOverHours,
    overtimeOverRate: values.overtimeOverRate,
    statutoryHolidayRate: values.statutoryHolidayRate,
    nonStatutoryHolidayRate: values.nonStatutoryHolidayRate,
    nightRate: values.nightRate,
    retirementPlan: values.retirementPlan,
    allowances: values.allowances,
    socialInsuranceHealth: values.socialInsuranceHealth ? '1' : '0',
    socialInsurancePension: values.socialInsurancePension ? '1' : '0',
    employmentInsurance: values.employmentInsurance,
    siteManagerName: values.siteManagerName,
    contractDate: values.contractDate,
  };
}

function scheduleFromFlat(
  flat: Record<string, string>,
  prefix: 'schedule1' | 'schedule2'
): WorkScheduleRow {
  const ampm = (v: string | undefined, fallback: AmPm): AmPm =>
    v === 'am' || v === 'pm' ? v : fallback;
  return {
    startHour: sanitizeHour12(flat[`${prefix}_startHour`] ?? ''),
    startMinute: sanitizeMinute(flat[`${prefix}_startMinute`] ?? ''),
    startAmPm: ampm(flat[`${prefix}_startAmPm`], 'am'),
    endHour: sanitizeHour12(flat[`${prefix}_endHour`] ?? ''),
    endMinute: sanitizeMinute(flat[`${prefix}_endMinute`] ?? ''),
    endAmPm: ampm(flat[`${prefix}_endAmPm`], 'pm'),
    breakMinutes: sanitizeBreakMinutes(flat[`${prefix}_breakMinutes`] ?? ''),
  };
}

export function employmentContractFromFlatMap(
  flat: Record<string, string> | undefined
): EmploymentContractAdminValues {
  const base = defaultEmploymentContractAdminValues();
  if (!flat || Object.keys(flat).length === 0) return base;

  const term =
    flat.employmentTermType === 'fixed' || flat.employmentTermType === 'indefinite'
      ? flat.employmentTermType
      : base.employmentTermType;
  const prefSuffix =
    flat.workplacePrefectureSuffix === '都' ||
    flat.workplacePrefectureSuffix === '道' ||
    flat.workplacePrefectureSuffix === '府' ||
    flat.workplacePrefectureSuffix === '県'
      ? flat.workplacePrefectureSuffix
      : base.workplacePrefectureSuffix;
  const citySuffix =
    flat.workplaceCitySuffix === '区' ||
    flat.workplaceCitySuffix === '市' ||
    flat.workplaceCitySuffix === '郡'
      ? flat.workplaceCitySuffix
      : base.workplaceCitySuffix;
  const retirement =
    flat.retirementPlan === 'chutaikyo' ||
    flat.retirementPlan === 'kentaikyo' ||
    flat.retirementPlan === 'none'
      ? flat.retirementPlan
      : base.retirementPlan;

  return {
    ...base,
    employmentTermType: term,
    termStartDate: flat.termStartDate ?? '',
    termEndDate: flat.termEndDate ?? '',
    termReason: sanitizeText(flat.termReason ?? '', EMP_CONTRACT_TEXT_LIMITS.termReason),
    workplacePrefectureName: sanitizeText(
      flat.workplacePrefectureName ?? '',
      EMP_CONTRACT_TEXT_LIMITS.workplacePrefectureName
    ),
    workplacePrefectureSuffix: prefSuffix,
    workplaceCityName: sanitizeText(
      flat.workplaceCityName ?? '',
      EMP_CONTRACT_TEXT_LIMITS.workplaceCityName
    ),
    workplaceCitySuffix: citySuffix,
    workplaceSite: sanitizeText(flat.workplaceSite ?? '', EMP_CONTRACT_TEXT_LIMITS.workplaceSite),
    jobDescription: sanitizeText(
      flat.jobDescription ?? '',
      EMP_CONTRACT_TEXT_LIMITS.jobDescription
    ),
    shiftSystem: flat.shiftSystem === 'yes' ? 'yes' : 'no',
    overtimeWork: flat.overtimeWork === 'yes' ? 'yes' : 'no',
    schedule1Enabled:
      flat.schedule1Enabled !== undefined
        ? flat.schedule1Enabled === '1'
        : flat.activeSchedule !== '2',
    schedule2Enabled:
      flat.schedule2Enabled !== undefined
        ? flat.schedule2Enabled === '1'
        : flat.activeSchedule === '2',
    schedule1: scheduleFromFlat(flat, 'schedule1'),
    schedule2: scheduleFromFlat(flat, 'schedule2'),
    dailyWorkHours: sanitizeDailyWorkHours(flat.dailyWorkHours ?? ''),
    baseSalary: sanitizeYenAmount(flat.baseSalary ?? ''),
    compensationPerDay: sanitizeCompensationDays(flat.compensationPerDay ?? ''),
    overtimeWithinHours: sanitizeOvertimeHours(flat.overtimeWithinHours ?? ''),
    overtimeWithinRate: sanitizePercentRate(flat.overtimeWithinRate ?? ''),
    overtimeOverHours: sanitizeOvertimeHours(flat.overtimeOverHours ?? ''),
    overtimeOverRate: sanitizePercentRate(flat.overtimeOverRate ?? ''),
    statutoryHolidayRate: sanitizePercentRate(flat.statutoryHolidayRate ?? ''),
    nonStatutoryHolidayRate: sanitizePercentRate(flat.nonStatutoryHolidayRate ?? ''),
    nightRate: sanitizePercentRate(flat.nightRate ?? ''),
    retirementPlan: retirement,
    allowances: sanitizeText(flat.allowances ?? '', EMP_CONTRACT_TEXT_LIMITS.allowances),
    socialInsuranceHealth: flat.socialInsuranceHealth === '1',
    socialInsurancePension: flat.socialInsurancePension === '1',
    employmentInsurance: flat.employmentInsurance === 'yes' ? 'yes' : 'no',
    siteManagerName: sanitizePersonName(
      flat.siteManagerName ?? '',
      EMP_CONTRACT_TEXT_LIMITS.siteManagerName
    ),
    contractDate: flat.contractDate ?? '',
  };
}

export function isEmploymentContractContextComplete(
  fields: Record<string, string> | undefined
): boolean {
  if (!fields) return false;
  // Draft rule: considered filled once any meaningful field is saved.
  const v = employmentContractFromFlatMap(fields);
  return Boolean(
    v.jobDescription.trim() ||
      v.workplacePrefectureName.trim() ||
      v.baseSalary.trim() ||
      v.siteManagerName.trim() ||
      v.termStartDate ||
      v.contractDate
  );
}
